const recordAliases = {
  'atv adventure': 'atv',
  'utv adventure': 'utv',
  'valley of fire': 'valley of fire',
  'u2 at sphere': 'u2',
  'hiking zion national park': 'zion',
  'eagles at sphere': 'eagles',
  'exploring atlantis': 'atlantis',
  'catamaran sailing': 'catamaran',
  'exploring coba': 'coba',
  'ferry to manly beach': 'manly',
  'coogee to bondi coastal walk': 'coogee bondi',
  'scenic world blue mountains': 'scenic world',
  'historic pub crawl': 'pub crawl',
  'snowmobiling langjokull': 'snowmobiling',
  'south coast to reykjavik': 'south coast',
};

const ignoredWords = new Set(['a', 'an', 'and', 'at', 'in', 'of', 'on', 'the', 'to']);

const normalize = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const searchableText = (item = {}) => normalize([
  item.id,
  item.entityId,
  item.title,
  item.name,
  item.heading,
  item.alt,
  item.caption,
  item.description,
  item.eventDescription,
].filter(Boolean).join(' '));

const targetFor = (title) => recordAliases[normalize(title)] || normalize(title);

const matchesRecordIdentity = (candidate, title) => {
  const identity = normalize([
    candidate.id, candidate.entityId, candidate.title, candidate.name, candidate.heading,
  ]
    .filter(Boolean).join(' '));
  const target = targetFor(title);
  return Boolean(identity && target && (identity.includes(target) || target.includes(identity)));
};

const matchesRecord = (candidate, title) => {
  const candidateText = searchableText(candidate);
  const target = targetFor(title);
  if (!candidateText || !target) return false;
  if (candidateText.includes(target) || target.includes(candidateText)) return true;

  const tokens = target.split(' ').filter((token) => token.length > 2 && !ignoredWords.has(token));
  return tokens.length > 0 && tokens.every((token) => candidateText.includes(token));
};

const normalizeMedia = (asset, index, recordId) => {
  if (!asset) return null;
  if (typeof asset === 'string') {
    return { id: `${recordId}-media-${index + 1}`, src: asset, alt: '' };
  }

  const src = asset.src || asset.image?.src || asset.image;
  if (!src) return null;
  return {
    ...asset,
    id: asset.id || `${recordId}-media-${index + 1}`,
    src,
    alt: asset.alt || asset.imageAlt || '',
  };
};

const mediaForItem = (item, recordId) => [
  ...(item.images || []),
  ...(item.videos || []).map((video) => ({ ...video, type: 'video' })),
  item.cardImage,
  item.image ? {
    src: item.image,
    alt: item.imageAlt,
    caption: item.imageCaption,
  } : null,
].filter(Boolean).map((asset, index) => normalizeMedia(asset, index, recordId)).filter(Boolean);

const yearFor = (record) => (
  String(record.tripSlug || record.date || record.id).match(/20\d{2}/)?.[0] || ''
);

const eventItems = (event, statKey) => {
  const preferred = statKey === 'team-dinners' ? ['restaurants', 'restaurantVisits'] : ['activities'];
  const fallback = [
    'restaurants', 'restaurantVisits', 'activities', 'places', 'sessions', 'venues', 'hotels',
  ];
  return [...new Set([...preferred, ...fallback])].flatMap((key) => event[key] || []);
};

const filteredGalleryMedia = (media, record, recordId, statKey) => {
  const matches = media.filter((asset) => matchesRecord(asset, record.title));
  const year = yearFor(record);
  const yearMatches = year && statKey === 'team-dinners'
    ? matches.filter((asset) => searchableText(asset).includes(year))
    : [];
  return (yearMatches.length ? yearMatches : matches)
    .map((asset, index) => normalizeMedia(asset, index, recordId))
    .filter(Boolean);
};

const uniqueMedia = (media) => {
  const seen = new Set();
  return media.filter((asset) => {
    if (!asset?.src || seen.has(asset.src)) return false;
    seen.add(asset.src);
    return true;
  });
};

export const resolvePassportRecordDetails = ({
  record, statKey, event, gallery,
}) => {
  if (!record) {
    return { description: '', media: [] };
  }

  const candidates = eventItems(event || {}, statKey);
  const matchingItem = candidates.find((candidate) => matchesRecordIdentity(candidate, record.title))
    || candidates.find((candidate) => matchesRecord(candidate, record.title));
  const sourceMedia = event?.gallery || gallery?.media || [];
  const taggedMedia = filteredGalleryMedia(sourceMedia, record, record.id, statKey);
  const directMedia = matchingItem ? mediaForItem(matchingItem, record.id) : [];
  const suppliedMedia = (record.media || [])
    .map((asset, index) => normalizeMedia(asset, index, record.id))
    .filter(Boolean);

  return {
    description: matchingItem?.description
      || matchingItem?.eventDescription
      || matchingItem?.summary
      || (statKey === 'badges'
        ? `This merit badge recognizes ${matchingItem?.title || record.title} during ${record.tripName}.`
        : '')
      || record.description,
    media: uniqueMedia([
      ...(taggedMedia.length ? taggedMedia : directMedia),
      ...suppliedMedia,
    ]),
    websiteUrl: matchingItem?.websiteUrl || '',
  };
};

export const supportsPassportRecordDetail = (statKey) => (
  ['adventures', 'badges', 'destinations', 'team-dinners'].includes(statKey)
);
